import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/registry/reapit/lib/utils'

/*
 * Empty State — DS alignment (Phase 3C)
 *
 * DS Page: 8403106
 *
 * Typography: EmptyTitle maps to DS "Description" (the primary label).
 *   DS uses a prominent short label — text-lg (= 20px DS L, Semi Bold) is appropriate.
 *   Previously text-lg resolved to 18px (Tailwind default); it now resolves to 20px
 *   per the Phase 2 typography scale update.
 *
 * Icon/media: DS icon-lg = 24px (size-6) for hero / large CTA contexts.
 *   EmptyMedia 'icon' variant: size-10 container with size-6 icon inside.
 *
 * Layout: centred column, gap-6 between major regions, gap-2 within header.
 */

function Empty({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty"
      className={cn(
        'flex min-w-0 flex-1 flex-col items-center justify-center gap-6 rounded-lg border-dashed p-6 text-center text-balance md:p-12',
        className,
      )}
      {...props}
    />
  )
}

function EmptyHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-header"
      className={cn('flex max-w-sm flex-col items-center gap-2 text-center', className)}
      {...props}
    />
  )
}

const emptyMediaVariants = cva(
  'mb-2 flex shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        /* icon variant: size-10 container, DS icon-lg (size-6 = 24px) icon inside */
        icon: "flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground [&_svg:not([class*='size-'])]:size-6",
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function EmptyMedia({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof emptyMediaVariants>) {
  return (
    <div
      data-slot="empty-icon"
      data-variant={variant}
      className={cn(emptyMediaVariants({ variant, className }))}
      {...props}
    />
  )
}

/*
 * EmptyTitle — DS "Description" label (primary headline of the empty state).
 * text-lg = 20px (DS L) in the updated typography scale. font-semibold per DS Semi Bold.
 */
function EmptyTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="empty-title" className={cn('text-lg font-semibold tracking-tight', className)} {...props} />
}

function EmptyDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <div
      data-slot="empty-description"
      className={cn(
        'text-sm/relaxed text-muted-foreground [&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary',
        className,
      )}
      {...props}
    />
  )
}

function EmptyContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-content"
      className={cn('flex w-full max-w-sm min-w-0 flex-col items-center gap-4 text-sm text-balance', className)}
      {...props}
    />
  )
}

export { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyContent, EmptyMedia }
